import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Mail, MapPin } from 'lucide-react';
import { FaInstagram, FaThreads } from 'react-icons/fa6';

const LOGO_URL = '/logo.png';

const Footer = () => {
  return (
    <footer className="bg-neutral-900 text-white" data-testid="footer">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img 
              src={LOGO_URL} 
              alt="Explore MHC" 
              className="h-16 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-neutral-400 mb-6 leading-relaxed">
              Explore MHC is a locally focused directory connecting residents and visitors with businesses, restaurants, services, and events throughout Martinsville-Henry County VA including Ridgeway, Collinsville, Bassett, and surrounding areas.
            </p>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/exploremhc" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-primary transition-colors" data-testid="footer-facebook-icon" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com/exploremhc_" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-pink-600 transition-colors" data-testid="footer-instagram-icon" aria-label="Instagram">
                <FaInstagram className="w-5 h-5" />
              </a>
              <a href="https://threads.com/@exploremhc_" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-white hover:text-black transition-colors" data-testid="footer-threads-icon" aria-label="Threads">
                <FaThreads className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Directory */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Directory</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/businesses" className="text-neutral-400 hover:text-white transition-colors">
                  All Businesses
                </Link>
              </li>
              <li>
                <Link to="/minority-owned" className="text-neutral-400 hover:text-white transition-colors">
                  Minority-Owned
                </Link>
              </li>
              <li>
                <Link to="/events" className="text-neutral-400 hover:text-white transition-colors">
                  Events Calendar
                </Link>
              </li>
              <li>
                <Link to="/submit-business" className="text-neutral-400 hover:text-white transition-colors">
                  List Your Business
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-neutral-400 hover:text-white transition-colors">
                  Pricing
                </Link>
                <Link to="/how-to" className="text-neutral-400 hover:text-white transition-colors">
                  How To Guide
                </Link>
              </li>
            </ul>
          </div>

          {/* Account */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Account</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/register" className="text-neutral-400 hover:text-white transition-colors">
                  Create Account
                </Link>
              </li>
              <li>
                <Link to="/community-login" className="text-neutral-400 hover:text-white transition-colors">
                  Community Login
                </Link>
              </li>
              <li>
                <Link to="/business-login" className="text-neutral-400 hover:text-white transition-colors">
                  Business Login
                </Link>
              </li>
              <li>
                <Link to="/submit-event" className="text-neutral-400 hover:text-white transition-colors">
                  Submit Event
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                <span className="text-neutral-400">
                  Martinsville & Henry County, Virginia
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-accent flex-shrink-0" />
                <a href="mailto:submissions@exploremhc.com" className="text-neutral-400 hover:text-white transition-colors">
                  submissions@exploremhc.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Facebook className="w-5 h-5 text-accent flex-shrink-0" />
                <a href="https://www.facebook.com/exploremhc" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white transition-colors" data-testid="footer-facebook">
                  Facebook
                </a>
              </li>
              <li className="flex items-center gap-3">
                <FaInstagram className="w-5 h-5 text-accent flex-shrink-0" />
                <a href="https://instagram.com/exploremhc_" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white transition-colors" data-testid="footer-instagram">
                  Instagram
                </a>
              </li>
              <li className="flex items-center gap-3">
                <FaThreads className="w-5 h-5 text-accent flex-shrink-0" />
                <a href="https://threads.com/@exploremhc_" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white transition-colors" data-testid="footer-threads">
                  Threads
                </a>
              </li>
            </ul>
          </div>
        </div>

        <hr className="border-neutral-800 my-12" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-neutral-500 text-sm">
            © {new Date().getFullYear()} Explore MHC. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy-policy" className="text-neutral-500 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-conditions" className="text-neutral-500 hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Load Histats tracking
if (typeof window !== 'undefined') {
  window._Hasync = window._Hasync || [];
  window._Hasync.push(['Histats.start', '1,5012845,4,0,0,0,00010000']);
  window._Hasync.push(['Histats.fasi', '1']);
  window._Hasync.push(['Histats.track_hits', '']);
  const hs = document.createElement('script');
  hs.type = 'text/javascript';
  hs.async = true;
  hs.src = '//s10.histats.com/js15_as.js';
  (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(hs);
}

export default Footer;
